/**
 * CurriculumPicker — reusable cascading multi-select for Worksheet / Question Paper / Sample Paper builders.
 *
 * Flow (all cascading):
 *   Board → Session → Class → Subject → Book → Chapters (multi) → Topics (multi)
 *
 * On any selection change, calls onChange({
 *   book_ids: [...],
 *   chapter_ids: [...],
 *   topic_ids: [...],
 *   scope_summary: "Book · Ch 1, Ch 2 · Topic 1.1"
 * }).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { curriculumLibraryAPI } from '@/services/api';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, BookOpen, ListTree, CheckSquare, Square, Info } from 'lucide-react';

const RESOURCE_TYPES = [
  { key: 'book', label: 'Textbook' },
  { key: 'syllabus', label: 'Syllabus' },
  { key: 'sample_paper', label: 'Sample Paper' },
  { key: 'reference', label: 'Reference' },
];

const CurriculumPicker = ({ onChange, defaultBoard = 'CBSE', defaultSession = '2025-26', defaultClass = '', defaultSubject = '' }) => {
  const [meta, setMeta] = useState({ boards: [], sessions: [], subjects: [], classes: [], resource_types: [] });
  const [filters, setFilters] = useState({
    board: defaultBoard, session: defaultSession,
    class_name: defaultClass, subject: defaultSubject,
    resource_type: 'book',
  });
  const [books, setBooks] = useState([]);
  const [selectedBookIds, setSelectedBookIds] = useState([]);
  const [chaptersByBook, setChaptersByBook] = useState({}); // { book_id: [chapters] }
  const [selectedChapterIds, setSelectedChapterIds] = useState([]);
  const [topicsByChapter, setTopicsByChapter] = useState({}); // { chapter_id: [topics] }
  const [selectedTopicIds, setSelectedTopicIds] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load meta once
  useEffect(() => {
    curriculumLibraryAPI.meta().then(({ data }) => setMeta(data)).catch(() => {});
  }, []);

  // Load books whenever filters change (any of board/session/class/subject/resource_type)
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    curriculumLibraryAPI.listBooks(filters).then(({ data }) => {
      if (cancelled) return;
      setBooks(data || []);
      setSelectedBookIds([]);
      setChaptersByBook({});
      setSelectedChapterIds([]);
      setTopicsByChapter({});
      setSelectedTopicIds([]);
    }).catch(() => setBooks([])).finally(() => setLoading(false));
    return () => { cancelled = true; };
  }, [filters]);

  // Notify parent on any change
  useEffect(() => {
    const bookNames = books.filter((b) => selectedBookIds.includes(b.book_id)).map((b) => b.book_name);
    const chapters = Object.values(chaptersByBook).flat().filter((c) => selectedChapterIds.includes(c.chapter_id));
    const topics = Object.values(topicsByChapter).flat().filter((t) => selectedTopicIds.includes(t.topic_id));
    const scope_summary = [
      bookNames.length ? `Books: ${bookNames.slice(0, 3).join(', ')}` : '',
      chapters.length ? `Chapters (${chapters.length}): ${chapters.slice(0, 4).map((c) => c.title).join(', ')}` : '',
      topics.length ? `Topics (${topics.length}): ${topics.slice(0, 4).map((t) => t.title).join(', ')}` : '',
    ].filter(Boolean).join(' • ');
    onChange?.({
      book_ids: selectedBookIds,
      chapter_ids: selectedChapterIds,
      topic_ids: selectedTopicIds,
      scope_summary,
      filters, // pass through so parent knows class/subject/board for prompt injection
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBookIds, selectedChapterIds, selectedTopicIds]);

  const toggleBook = async (book) => {
    const isSelected = selectedBookIds.includes(book.book_id);
    if (isSelected) {
      // Deselect book + all its chapters + all its topics
      const chIds = (chaptersByBook[book.book_id] || []).map((c) => c.chapter_id);
      setSelectedBookIds(selectedBookIds.filter((id) => id !== book.book_id));
      setSelectedChapterIds(selectedChapterIds.filter((id) => !chIds.includes(id)));
      setSelectedTopicIds(selectedTopicIds.filter((tid) => !Object.entries(topicsByChapter).some(([cid, tps]) => chIds.includes(cid) && tps.some((t) => t.topic_id === tid))));
    } else {
      setSelectedBookIds([...selectedBookIds, book.book_id]);
      if (!chaptersByBook[book.book_id]) {
        try {
          const { data } = await curriculumLibraryAPI.listChapters(book.book_id);
          setChaptersByBook((prev) => ({ ...prev, [book.book_id]: data || [] }));
        } catch { /* ignore */ }
      }
    }
  };

  const toggleChapter = async (chapter) => {
    const isSelected = selectedChapterIds.includes(chapter.chapter_id);
    if (isSelected) {
      const topicIds = (topicsByChapter[chapter.chapter_id] || []).map((t) => t.topic_id);
      setSelectedChapterIds(selectedChapterIds.filter((id) => id !== chapter.chapter_id));
      setSelectedTopicIds(selectedTopicIds.filter((tid) => !topicIds.includes(tid)));
    } else {
      setSelectedChapterIds([...selectedChapterIds, chapter.chapter_id]);
      if (!topicsByChapter[chapter.chapter_id]) {
        try {
          const { data } = await curriculumLibraryAPI.listTopics(chapter.chapter_id);
          setTopicsByChapter((prev) => ({ ...prev, [chapter.chapter_id]: data || [] }));
        } catch { /* ignore */ }
      }
    }
  };

  const toggleTopic = (topic) => {
    const isSelected = selectedTopicIds.includes(topic.topic_id);
    if (isSelected) setSelectedTopicIds(selectedTopicIds.filter((id) => id !== topic.topic_id));
    else setSelectedTopicIds([...selectedTopicIds, topic.topic_id]);
  };

  const availableChapters = useMemo(() =>
    selectedBookIds.flatMap((bid) => (chaptersByBook[bid] || []).map((c) => ({ ...c, __book: books.find((b) => b.book_id === bid) })))
  , [selectedBookIds, chaptersByBook, books]);
  const availableTopics = useMemo(() =>
    selectedChapterIds.flatMap((cid) => (topicsByChapter[cid] || []).map((t) => ({ ...t, __chapter: availableChapters.find((c) => c.chapter_id === cid) })))
  , [selectedChapterIds, topicsByChapter, availableChapters]);

  return (
    <Card className="p-5 border border-orange-200/70 bg-gradient-to-br from-orange-50/40 to-white" data-testid="curriculum-picker">
      <div className="flex items-center gap-2 mb-4">
        <ListTree className="w-5 h-5 text-orange-600" />
        <h3 className="text-base font-bold text-orange-800">Use Curriculum Library</h3>
        <span className="text-[11px] text-stone-500 ml-auto">Cascading Board → Book → Chapters → Topics</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <div>
          <Label className="text-xs">Board</Label>
          <Select value={filters.board} onValueChange={(v) => setFilters({ ...filters, board: v })}>
            <SelectTrigger data-testid="cp-board" className="mt-1 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>{(meta.boards || []).map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Session</Label>
          <Select value={filters.session} onValueChange={(v) => setFilters({ ...filters, session: v })}>
            <SelectTrigger data-testid="cp-session" className="mt-1 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>{(meta.sessions || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Class</Label>
          <Select value={filters.class_name || 'any'} onValueChange={(v) => setFilters({ ...filters, class_name: v === 'any' ? '' : v })}>
            <SelectTrigger data-testid="cp-class" className="mt-1 h-9"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any class</SelectItem>
              {(meta.classes || []).map((c) => <SelectItem key={c} value={c}>Class {c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Subject</Label>
          <Select value={filters.subject || 'any'} onValueChange={(v) => setFilters({ ...filters, subject: v === 'any' ? '' : v })}>
            <SelectTrigger data-testid="cp-subject" className="mt-1 h-9"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any subject</SelectItem>
              {(meta.subjects || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Type</Label>
          <Select value={filters.resource_type} onValueChange={(v) => setFilters({ ...filters, resource_type: v })}>
            <SelectTrigger data-testid="cp-restype" className="mt-1 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>{RESOURCE_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Books column */}
        <div>
          <Label className="text-xs font-bold text-orange-800 flex items-center gap-1"><BookOpen className="w-3 h-3" /> Books ({books.length})</Label>
          <div className="mt-2 max-h-56 overflow-auto bg-white border border-stone-200 rounded p-2 space-y-1" data-testid="cp-books">
            {loading ? <div className="text-center py-4"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div> :
              books.length === 0 ? <p className="text-xs text-stone-400 py-4 text-center">No books match. Ask admin to upload.</p> :
              books.map((b) => (
                <button
                  key={b.book_id}
                  type="button"
                  data-testid={`cp-book-${b.book_id}`}
                  onClick={() => toggleBook(b)}
                  className={`w-full text-left text-xs px-2 py-1.5 rounded flex items-start gap-2 ${selectedBookIds.includes(b.book_id) ? 'bg-orange-100 border border-orange-300' : 'hover:bg-orange-50 border border-transparent'}`}
                >
                  {selectedBookIds.includes(b.book_id) ? <CheckSquare className="w-3.5 h-3.5 text-orange-600 mt-0.5" /> : <Square className="w-3.5 h-3.5 text-stone-400 mt-0.5" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{b.book_name}</div>
                    <div className="text-[10px] text-stone-500">Class {b.class_name} · {b.subject} · {b.chapter_count || 0} ch</div>
                  </div>
                </button>
              ))
            }
          </div>
        </div>

        {/* Chapters column */}
        <div>
          <Label className="text-xs font-bold text-orange-800">Chapters ({availableChapters.length})</Label>
          <div className="mt-2 max-h-56 overflow-auto bg-white border border-stone-200 rounded p-2 space-y-1" data-testid="cp-chapters">
            {availableChapters.length === 0 ? <p className="text-xs text-stone-400 py-4 text-center">Select a book</p> :
              availableChapters.map((c) => (
                <button
                  key={c.chapter_id}
                  type="button"
                  data-testid={`cp-chapter-${c.chapter_id}`}
                  onClick={() => toggleChapter(c)}
                  className={`w-full text-left text-xs px-2 py-1.5 rounded flex items-start gap-2 ${selectedChapterIds.includes(c.chapter_id) ? 'bg-amber-100 border border-amber-300' : 'hover:bg-amber-50 border border-transparent'}`}
                >
                  {selectedChapterIds.includes(c.chapter_id) ? <CheckSquare className="w-3.5 h-3.5 text-amber-600 mt-0.5" /> : <Square className="w-3.5 h-3.5 text-stone-400 mt-0.5" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{c.number ? `${c.number}. ` : ''}{c.title}</div>
                    <div className="text-[10px] text-stone-500 truncate">{c.__book?.book_name}</div>
                  </div>
                </button>
              ))
            }
          </div>
        </div>

        {/* Topics column */}
        <div>
          <Label className="text-xs font-bold text-orange-800">Topics ({availableTopics.length})</Label>
          <div className="mt-2 max-h-56 overflow-auto bg-white border border-stone-200 rounded p-2 space-y-1" data-testid="cp-topics">
            {availableTopics.length === 0 ? <p className="text-xs text-stone-400 py-4 text-center">Select a chapter</p> :
              availableTopics.map((t) => (
                <button
                  key={t.topic_id}
                  type="button"
                  data-testid={`cp-topic-${t.topic_id}`}
                  onClick={() => toggleTopic(t)}
                  className={`w-full text-left text-xs px-2 py-1.5 rounded flex items-start gap-2 ${selectedTopicIds.includes(t.topic_id) ? 'bg-emerald-100 border border-emerald-300' : 'hover:bg-emerald-50 border border-transparent'}`}
                >
                  {selectedTopicIds.includes(t.topic_id) ? <CheckSquare className="w-3.5 h-3.5 text-emerald-600 mt-0.5" /> : <Square className="w-3.5 h-3.5 text-stone-400 mt-0.5" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{t.number ? `${t.number}. ` : ''}{t.title}</div>
                  </div>
                </button>
              ))
            }
          </div>
        </div>
      </div>

      {(selectedBookIds.length + selectedChapterIds.length + selectedTopicIds.length) > 0 && (
        <div className="mt-3 flex items-start gap-2 text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-3 py-2" data-testid="cp-scope-summary">
          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>
            <strong>AI Scope Lock:</strong> {selectedBookIds.length} book(s), {selectedChapterIds.length} chapter(s),
            {' '}{selectedTopicIds.length} topic(s). The AI will strictly stay within this scope.
          </span>
        </div>
      )}
    </Card>
  );
};

export default CurriculumPicker;
